import { useMemo } from "react";
import { useGetCheckAccessFunction, useRoutesContext } from "../components/generic";
import { MenuItemDescription } from "../components/ui";
import { RouteDescription } from "../types";

export type ExtendedMenuItemDescription = Omit<MenuItemDescription, "items"> & {
  id: string,
  items?: ExtendedMenuItemDescription[]
  requiredPermissions?: string | string[]
  requiredOneOfPermissions?: string | string[]
}

export const mapRoutesToMenuItems = (routes?: RouteDescription[]): ExtendedMenuItemDescription[] =>
  routes
    ?.map(x => {
      const items = mapRoutesToMenuItems(x.children);
      return ({
        id: (x.menuItem?.showInMainMenu ?? true) && x.menuItem?.id,
        label: x.menuItem?.label,
        icon: x.menuItem?.icon,
        category: x.menuItem?.category,
        showCategory: x.menuItem?.showCategory,
        path: x.menuItem?.path ?? x.path,
        externalPath: x.menuItem?.externalPath,
        items: items,
        requiredPermissions: x.requiredPermissions,
        requiredOneOfPermissions: Array.from(new Set([
          ...(Array.isArray(x.requiredOneOfPermissions) ? x.requiredOneOfPermissions : [ x.requiredOneOfPermissions ]),
          ...(Object.values(x.pageModePermissions || {})
            .flatMap(y => [
              ...(Array.isArray(y.requiredPermissions) ? y.requiredPermissions : [ y.requiredPermissions ]),
              ...(Array.isArray(y.requiredOneOfPermissions) ? y.requiredOneOfPermissions : [ y.requiredOneOfPermissions ]),
            ])
          ),
          ...(items.flatMap(x => x.requiredPermissions)),
          ...(items.flatMap(x => x.requiredOneOfPermissions)),
        ].filter(x => x))),
      }) as ExtendedMenuItemDescription;
    })
    .flatMap(x => x.id ? [ x ] : x.items || []) || [];

const filterAvailableMenuItems = (
  menuItems: ExtendedMenuItemDescription[] | undefined,
  checkItemAccess: (requiredPermissions?: (string | string[]), requiredOneOfPermissions?: (string | string[])) => {
    pending: boolean;
    isAccessAllowed: boolean
  },
): ExtendedMenuItemDescription[] =>
  menuItems?.filter(x => checkItemAccess(x.requiredPermissions, x.requiredOneOfPermissions).isAccessAllowed)
    .map(x => ({
      ...x,
      items: filterAvailableMenuItems(x.items, checkItemAccess),
    })) || [];


export function useBuildMenu(): MenuItemDescription[] {
  const routes = useRoutesContext();
  const menu = useMemo(() => mapRoutesToMenuItems(routes), [ routes ]);
  const checkItemAccess = useGetCheckAccessFunction();
  return useMemo(() => filterAvailableMenuItems(menu, checkItemAccess), [ menu, checkItemAccess ]);
}